import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  MaCircleButton, MaIcon, MaImageSlot, MaLiveBadge, MaSectionHeader, MaStat,
} from "../components/ui";
import { useAuthStatus, useCheckin, useSaves, useToggleSave, useVenue } from "../lib/queries";
import { directionsUrl, share } from "../lib/share";
import { MA_CATS } from "@myams/shared";

function Toast({ msg }: { msg: string }) {
  return (
    <div style={{
      position: "absolute", bottom: 100, left: "50%", transform: "translateX(-50%)",
      background: "var(--ma-ink)", color: "var(--ma-paper)",
      padding: "10px 16px", borderRadius: "var(--ma-card-radius)",
      fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
      textTransform: "uppercase", zIndex: 50,
      boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
    }}>{msg}</div>
  );
}

export function Venue() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: venue, isPending } = useVenue(id);
  const { data: saves = [] } = useSaves();
  const toggleSave = useToggleSave();
  const { status: authStatus } = useAuthStatus();
  const checkin = useCheckin();
  const [toast, setToast] = useState<string | null>(null);
  const [checkinNote, setCheckinNote] = useState("");
  const [showCheckin, setShowCheckin] = useState(false);

  if (isPending || !venue) {
    return (
      <div className="ma-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", letterSpacing: "0.16em" }}>
          loading…
        </span>
      </div>
    );
  }

  const isSaved = saves.some((s) => s.venue_id === venue.id);
  const cat = MA_CATS[venue.cat] ?? MA_CATS.all;

  const onShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : `https://myams.pages.dev/venue/${venue.id}`;
    const outcome = await share({ title: venue.name, text: venue.now || venue.neigh, url });
    if (outcome === "copied") setToast("Link copied");
    else if (outcome === "shared") setToast("Shared");
    else setToast("Couldn't share");
    setTimeout(() => setToast(null), 1800);
  };

  const onDirections = () => {
    if (venue.lat == null || venue.lng == null) {
      setToast("No location set");
      setTimeout(() => setToast(null), 1800);
      return;
    }
    window.open(directionsUrl(venue.lat, venue.lng, venue.name), "_blank", "noopener");
  };

  const onCheckin = async () => {
    if (authStatus !== "authed") {
      navigate("/login", { state: { returnTo: `/venue/${venue.id}` } });
      return;
    }
    if (!showCheckin) { setShowCheckin(true); return; }
    try {
      await checkin.mutateAsync({ venueId: venue.id, note: checkinNote.trim() || undefined });
      setToast("Checked in!");
      setCheckinNote("");
      setShowCheckin(false);
    } catch {
      setToast("Couldn't check in");
    }
    setTimeout(() => setToast(null), 1800);
  };

  return (
    <div className="ma-screen" style={{ overflowY: "auto", paddingBottom: 110 }}>
      <div style={{ position: "relative", height: 360, overflow: "hidden" }}>
        <MaImageSlot height={360} tone={cat.id === "music" ? "brick" : cat.id === "art" ? "canal" : "mustard"} src={venue.coverUrl} category={venue.cat} fallbackInitial={venue.name} />
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to bottom, rgba(20,12,4,0.45) 0%, transparent 30%, transparent 60%, rgba(20,12,4,0.55) 100%)",
        }} />

        <div style={{
          position: "absolute", top: 0, left: 0, right: 0,
          padding: "calc(env(safe-area-inset-top, 20px) + 34px) 14px 0",
          display: "flex", justifyContent: "space-between",
        }}>
          <MaCircleButton icon="back" label="Back" onClick={() => navigate(-1)} />
          <div style={{ display: "flex", gap: 8 }}>
            <MaCircleButton icon="share" label="Share" onClick={onShare} />
            <MaCircleButton
              icon="bookmark"
              label={isSaved ? "Saved" : "Save"}
              dark={isSaved}
              onClick={() => {
                if (authStatus !== "authed") {
                  navigate("/login", { state: { returnTo: `/venue/${venue.id}` } });
                  return;
                }
                toggleSave.mutate({ venueId: venue.id, saved: isSaved });
              }}
            />
          </div>
        </div>

        {venue.live && (
          <div style={{ position: "absolute", top: 116, left: 16 }}>
            <MaLiveBadge timeLeft={venue.hoursToday ? `until ${venue.hoursToday.split("–").pop()?.trim() ?? ""}` : undefined} />
          </div>
        )}
      </div>

      <div style={{
        background: "var(--ma-paper)", padding: "20px 20px 16px",
        marginTop: -16, borderTopLeftRadius: 22, borderTopRightRadius: 22,
        position: "relative", zIndex: 2,
      }}>
        <div className="ma-mono" style={{
          fontSize: 10, letterSpacing: "0.18em",
          textTransform: "uppercase", color: cat.color, marginBottom: 6,
        }}>
          {cat.label} · {venue.neigh}{venue.established ? ` · since ${venue.established}` : ""}
        </div>
        <div className="ma-display" style={{ fontSize: 38, color: "var(--ma-ink)", lineHeight: 0.92, marginBottom: 8 }}>
          {venue.name}
        </div>
        {venue.lat != null && venue.lng != null && (
          <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", letterSpacing: "0.06em" }}>
            {venue.lat.toFixed(4)}, {venue.lng.toFixed(4)}
          </div>
        )}
      </div>

      {venue.now && (
        <div style={{ padding: "8px 16px 16px" }}>
          <div style={{
            background: "var(--ma-ink)", color: "var(--ma-paper)",
            borderRadius: "var(--ma-card-radius)",
            padding: "16px 18px", position: "relative", overflow: "hidden",
          }}>
            <div className="ma-noise-dark" style={{ opacity: 0.3 }} />
            <div style={{ position: "relative", zIndex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span className="ma-pulse-dot" style={{ background: "var(--ma-brick)" }} />
                <span className="ma-mono" style={{
                  fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
                }}>
                  Happening now
                </span>
              </div>
              <div className="ma-display" style={{ fontSize: 22, color: "var(--ma-paper)" }}>
                {venue.now}
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ padding: "0 16px 18px", display: "flex", gap: 8 }}>
        <button
          onClick={onCheckin}
          disabled={checkin.isPending}
          style={{
            flex: 2, padding: "16px 0",
            background: "var(--ma-brick)", color: "var(--ma-paper)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-display)", fontWeight: 700, fontSize: 15,
            letterSpacing: "0.02em", cursor: "pointer",
            opacity: checkin.isPending ? 0.7 : 1,
          }}>
          {checkin.isPending ? "Saving…" : showCheckin ? "Confirm check-in" : "Check in"}
        </button>
        <button
          onClick={onDirections}
          aria-label="Directions"
          style={{
            flex: 1, padding: "16px 0",
            background: "var(--ma-cream)", color: "var(--ma-ink)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
            textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
          <MaIcon name="pin" size={14} stroke={1.8} />
          Go
        </button>
        <button
          onClick={onShare}
          aria-label="Share"
          style={{
            flex: 1, padding: "16px 0",
            background: "var(--ma-cream)", color: "var(--ma-ink)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
            textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
          }}>
          Share
        </button>
      </div>

      {showCheckin && (
        <div style={{ padding: "0 16px 18px" }}>
          <div style={{
            background: "var(--ma-cream)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            padding: "12px 14px",
          }}>
            <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.16em",
              textTransform: "uppercase", color: "var(--ma-ink-soft)", marginBottom: 8 }}>
              Add a note (optional)
            </div>
            <textarea
              value={checkinNote}
              onChange={(e) => setCheckinNote(e.target.value.slice(0, 280))}
              placeholder="how is it?"
              rows={2}
              autoFocus
              style={{
                width: "100%", border: "none", outline: "none",
                background: "transparent", resize: "none",
                fontFamily: "var(--ma-body)", fontSize: 14, color: "var(--ma-ink)",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
              <span className="ma-mono" style={{ fontSize: 10, color: "var(--ma-ink-soft)" }}>
                {checkinNote.length}/280
              </span>
              <button onClick={() => setShowCheckin(false)} style={{
                background: "transparent", border: "none", padding: 0, cursor: "pointer",
                fontFamily: "var(--ma-mono)", fontSize: 10, letterSpacing: "0.1em",
                textTransform: "uppercase", color: "var(--ma-ink-soft)", textDecoration: "underline",
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {(venue.established || venue.capacity || venue.price) && (
        <div style={{ padding: "0 16px 18px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(0, 1fr))", gap: 8 }}>
          {venue.established && <MaStat value={venue.established} label="Established" />}
          {venue.capacity && <MaStat value={venue.capacity >= 1000 ? `${(venue.capacity / 1000).toFixed(1)}k` : String(venue.capacity)} label="Capacity" />}
          {venue.price && <MaStat value={venue.price} label="Price" accent />}
        </div>
      )}

      {venue.about && (
        <div style={{ padding: "0 16px 18px" }}>
          <MaSectionHeader label="About" />
          <div style={{
            fontFamily: "var(--ma-body)", fontSize: 14.5, lineHeight: 1.5,
            color: "var(--ma-ink)",
          }}>
            {venue.about}
          </div>
        </div>
      )}

      {(venue.address || venue.hoursToday || venue.addressFull) && (
        <div style={{ padding: "0 16px 18px" }}>
          <MaSectionHeader label="Practical" />
          <div style={{
            background: "var(--ma-cream)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            overflow: "hidden",
          }}>
            {[
              venue.address && (["Address", venue.address] as const),
              !venue.address && venue.addressFull && (["Address", venue.addressFull] as const),
              venue.hoursToday && (["Hours tonight", venue.hoursToday] as const),
            ].filter(Boolean).map((row, i, arr) => {
              const [k, v] = row as [string, string];
              return (
                <div key={k} style={{
                  display: "flex", padding: "12px 16px",
                  borderBottom: i === arr.length - 1 ? "none" : "1px dashed var(--ma-line)",
                }}>
                  <div className="ma-mono" style={{
                    fontSize: 10, letterSpacing: "0.12em",
                    textTransform: "uppercase", color: "var(--ma-ink-soft)", width: 130,
                  }}>
                    {k}
                  </div>
                  <div style={{ fontFamily: "var(--ma-body)", fontSize: 13.5, color: "var(--ma-ink)", flex: 1 }}>
                    {v}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {toast && <Toast msg={toast} />}
    </div>
  );
}
